import type { CSSProperties } from "react";

import Header from "../../components/layout/Header/Header";
import Footer from "../../components/layout/Footer/Footer";
import Container from "../../components/layout/Container/Container";

import s from "./ContactsPage.module.scss";

import { useClinicInfo } from "../../api/queries/useClinicInfo";
import { toMap } from "../../app/clinicInfoMap";

import YandexMap from "../../components/maps/YandexMap/YandexMap";

import { ReactComponent as PhoneIcon } from "../../assets/ui/phone.svg";
import { ReactComponent as PinIcon } from "../../assets/ui/pin.svg";
import { ReactComponent as ClockIcon } from "../../assets/ui/clock.svg";
import { ReactComponent as RouteIcon } from "../../assets/ui/route.svg";

type SocialItem = {
  key: string;
  href: string;
  label: string;
  iconSrc: string;
};

function toTel(phone: string) {
  return phone.replace(/[^\d+]/g, "");
}

function uniq(arr: string[]) {
  return Array.from(new Set(arr.map((x) => x.trim()).filter(Boolean)));
}

function parsePhones(cfg: Record<string, string | undefined>) {
  const phones: string[] = [];

  if (cfg["contacts.phone"]) phones.push(cfg["contacts.phone"]);
  if (cfg["contacts.phone2"]) phones.push(cfg["contacts.phone2"]);
  if (cfg["contacts.phone3"]) phones.push(cfg["contacts.phone3"]);

  const raw = cfg["contacts.phones"];
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        parsed.forEach((p) => typeof p === "string" && phones.push(p));
      }
    } catch {
      // ignore
    }
  }

  return uniq(phones);
}

function buildYandexQueryLink(address: string) {
  const q = encodeURIComponent(address || "стоматология");
  return `https://yandex.ru/maps/?text=${q}`;
}

function toNum(v: unknown) {
  const n = typeof v === "string" ? Number(v) : Number.NaN;
  return Number.isFinite(n) ? n : undefined;
}

function normalizeExternalLink(href: string) {
  const trimmed = href.trim();
  if (!trimmed) return "";

  if (/^(https?:)?\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed.replace(/^\/+/, "")}`;
}

function getIconMaskStyle(iconSrc: string): CSSProperties {
  return {
    WebkitMaskImage: `url(${iconSrc})`,
    maskImage: `url(${iconSrc})`,
  };
}

const FALLBACK = {
  phones: ["+7 (999) 111-22-33", "+7 (999) 444-55-66"],
  address: "Москва, ул. Льва Толстого, 16",
  hours: "Пн–Сб 9:00–20:00, Вс выходной",
  mapTitle: "Ю-ДЕНТ (тест)",
  coords: [55.733835, 37.588227] as [number, number],
  zoom: 16,
};

export default function ContactsPage() {
  const { data, isLoading, isError } = useClinicInfo();
  const cfg = toMap(data ?? []) as Record<string, string | undefined>;

  const hasDbData = !isLoading && !isError && (data?.length ?? 0) > 0;

  const dbPhones = hasDbData ? parsePhones(cfg) : [];
  const phones = dbPhones.length > 0 ? dbPhones : FALLBACK.phones;

  const addressFromDb = hasDbData ? (cfg["contacts.address"] || "").trim() : "";
  const hoursFromDb = hasDbData ? (cfg["contacts.hours"] || "").trim() : "";

  const address = addressFromDb || FALLBACK.address;
  const hours = hoursFromDb || FALLBACK.hours;

  const mapLinkFromDb = hasDbData ? (cfg["contacts.mapLink"] || "").trim() : "";
  const mapLink = mapLinkFromDb || buildYandexQueryLink(address);

  const lat = hasDbData ? toNum(cfg["contacts.map.lat"]) : undefined;
  const lng = hasDbData ? toNum(cfg["contacts.map.lng"]) : undefined;

  const coords =
    lat !== undefined && lng !== undefined
      ? ([lat, lng] as [number, number])
      : FALLBACK.coords;

  const mapTitleFromDb = hasDbData ? (cfg["contacts.map.title"] || "").trim() : "";
  const mapTitle = mapTitleFromDb || FALLBACK.mapTitle;

  const zoomFromDb = hasDbData ? toNum(cfg["contacts.map.zoom"]) : undefined;
  const zoom = zoomFromDb ?? FALLBACK.zoom;

  const socialItems: SocialItem[] = [
    {
      key: "vk",
      href: (cfg["contacts.social.vk"] || "").trim(),
      label: "VK",
      iconSrc: "/icons/vk.svg",
    },
    {
      key: "max",
      href: (cfg["contacts.social.max"] || "").trim(),
      label: "MAX",
      iconSrc: "/icons/max.svg",
    },
  ].filter((item) => Boolean(item.href));

  const usingFallback = !hasDbData;

  return (
    <div className={s.pageWrap}>
      <Header />

      <main className={s.main}>
        <section className="section section--tight">
          <Container>
            <div className={s.panel}>
              <h1 className={s.h1}>Контакты</h1>

              <p className={s.p}>
                Позвоните или напишите — ответим и подберем удобное для Вас время.
              </p>

              {usingFallback && (
                <div className={s.meta}>
                  Сейчас показаны тестовые данные (clinic-info пустой или сервер недоступен).
                </div>
              )}

              <div className={s.grid}>
                <div className={s.col}>
                  <div className={s.card}>
                    <div className={s.hRow}>
                      <PhoneIcon className={s.hIcon} aria-hidden="true" />
                      <span className={s.cardTitle}>Телефоны</span>
                    </div>

                    {isLoading && <div className={s.meta}>Загрузка данных…</div>}
                    {isError && (
                      <div className={s.meta}>
                        Нет связи с сервером (показаны тестовые данные)
                      </div>
                    )}

                    <ul className={s.phoneList}>
                      {phones.map((p) => (
                        <li key={p} className={s.phoneItem}>
                          <a className={s.phoneLink} href={`tel:${toTel(p)}`}>
                            {p}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className={s.card}>
                    <div className={s.hRow}>
                      <PinIcon className={s.hIcon} aria-hidden="true" />
                      <span className={s.cardTitle}>Адрес</span>
                    </div>
                    <div className={s.text}>{address}</div>
                  </div>

                  <div className={s.card}>
                    <div className={s.hRow}>
                      <ClockIcon className={s.hIcon} aria-hidden="true" />
                      <span className={s.cardTitle}>Режим работы</span>
                    </div>
                    <div className={s.text}>{hours}</div>
                  </div>

                  <div className={s.card}>
                    <div className={s.hRow}>
                      <RouteIcon className={s.hIcon} aria-hidden="true" />
                      <span className={s.cardTitle}>Как добраться</span>
                    </div>
                    <div className={s.text}>
                      <a className={s.mapLink} href={mapLink} target="_blank" rel="noreferrer">
                        Открыть в Яндекс.Картах
                      </a>
                    </div>
                  </div>

                  {socialItems.length > 0 && (
                    <div className={s.socials} aria-label="Социальные сети">
                      {socialItems.map((item) => (
                        <a
                          key={item.key}
                          href={normalizeExternalLink(item.href)}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={item.label}
                          title={item.label}
                          className={s.socialLink}
                        >
                          <span
                            className={`${s.socialIcon} ${s[`socialIcon_${item.key}`]}`}
                            style={getIconMaskStyle(item.iconSrc)}
                            aria-hidden="true"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                <div className={s.col}>
                  <div className={s.mapCard}>
                    <div className={s.hRow}>
                      <PinIcon className={s.hIcon} aria-hidden="true" />
                      <span className={s.cardTitle}>Мы на карте</span>
                    </div>

                    <div className={s.mapWrap}>
                      <YandexMap title={mapTitle} address={address} coords={coords} zoom={zoom} />
                    </div>

                    {!hasDbData && (
                      <div className={s.meta} style={{ marginTop: 10 }}>
                        Координаты/адрес сейчас тестовые. Потом перенесем в clinic-info
                        (contacts.map.lat / contacts.map.lng).
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>

      <Footer />
    </div>
  );
}
